// Equipment & Setup guides — full article content keyed by slug

export const equipmentArticles = {
  'water-filling-machine-buying-guide': {
    intro: [
      'The filling machine is the heart of your bottling plant — it sets your production speed, your labour requirement, and ultimately your cost per bottle. Choose too small and you will be re-investing within a year; choose too big and capital sits idle while EMIs run.',
      'This buying guide explains how to size a water filling machine correctly, what separates a good machine from a cheap one, and the specific questions to ask any supplier before you pay an advance.'
    ],
    sections: [
      {
        heading: 'Understanding BPM: How Capacity Is Measured',
        paras: [
          'Water filling machines are rated in BPM — Bottles Per Minute — almost always benchmarked on the 1-litre bottle. A 60 BPM machine fills 3,600 one-litre bottles per hour; switch to 500 ml bottles and output in bottles rises while litres fall slightly due to changeover mechanics.',
          'Be careful with inflated ratings: some suppliers quote peak mechanical speed rather than sustained production speed. Ask for the guaranteed output per 8-hour shift including CIP cleaning and changeover time — that number is what your business plan should use.'
        ]
      },
      {
        heading: 'Capacity Selection: Match the Machine to Your Market',
        paras: [
          'Work backwards from distribution. Estimate your realistic monthly sales at the end of year one, add 30-40% headroom, and divide by working hours. As a quick reference:'
        ],
        table: {
          headers: ['Machine', 'Output (1L)', 'Best Suited For', 'Price Range'],
          rows: [
            ['30 BPM', '1,800 BPH', 'Startups, single-town distribution', '₹8 – 12 Lakhs'],
            ['40 BPM', '2,400 BPH', 'District distribution, growing brands', '₹12 – 16 Lakhs'],
            ['60 BPM', '3,600 BPH', 'City-level commercial operations', '₹18 – 25 Lakhs'],
            ['90 BPM', '5,400 BPH', 'Multi-district regional brands', '₹28 – 38 Lakhs'],
            ['120 BPM', '7,200 BPH', 'State-level distribution', '₹45 – 60 Lakhs'],
            ['200 BPM', '12,000 BPH', 'National brands, co-packing', '₹80 Lakhs – 1.2 Crore']
          ]
        }
      },
      {
        heading: 'Rinsing-Filling-Capping: Why 3-in-1 Monoblock Wins',
        paras: [
          'Modern machines integrate three operations in one rotary monoblock: bottle rinsing (with treated water or ozonated water), filling, and capping. Compared to separate linear machines, a monoblock needs fewer operators, occupies less floor space, maintains hygiene (bottles never leave the enclosed zone open-mouthed), and synchronises speeds automatically.',
          'For any plant of 40 BPM and above, a rotary RFC monoblock is the standard we recommend. At 24-30 BPM, well-built linear automatic machines remain a cost-effective option.'
        ]
      },
      {
        heading: 'The Specification Checklist That Separates Good from Cheap',
        paras: [
          'Two machines with the same BPM rating can differ by lakhs in price — and the difference is usually in these details:'
        ],
        bullets: [
          'Contact parts in SS 304 (SS 316 for high-purity applications) — insist on material test certificates',
          'No bottle-no fill sensor and drip-proof filling valves to prevent wastage',
          'PLC control with touch-screen HMI, not just relay logic — critical for fault diagnosis',
          'Servo or variable-frequency drives for smooth speed regulation',
          'Enclosed cabinet with air filtration over the filling zone',
          'Cap elevator and sorter capacity matched to machine speed',
          'Guaranteed spare parts availability and documented service response time'
        ]
      },
      {
        heading: 'Semi-Automatic vs Fully Automatic: The Real Trade-Off',
        paras: [
          'Semi-automatic machines cost 30-40% less but need 3-4 additional operators per shift and produce less consistent fill levels and torque. Over three years, the labour cost difference typically exceeds the machinery saving — which is why we recommend automatic machines for any plant intending to run daily commercial production, reserving semi-automatic lines for jar (20-litre) filling where speeds are lower.',
          'Also weigh future BIS audits: automatic machines with enclosed filling zones make hygiene compliance visibly easier.'
        ]
      }
    ],
    faqs: [
      {
        q: 'Which water filling machine is best for a small plant?',
        a: 'For most startups, a 30 or 40 BPM automatic rinsing-filling-capping machine offers the best balance — ₹8 – 16 Lakhs investment, low manpower, and output sufficient for district-level distribution with room to grow via additional shifts.'
      },
      {
        q: 'What is the price of a water bottle filling machine in India?',
        a: 'Automatic machines range from ₹8 – 12 Lakhs for 30 BPM up to ₹80 Lakhs – 1.2 Crore for 200 BPM rotary lines. Price varies with automation level, filling heads, and build quality of contact parts.'
      },
      {
        q: 'How many operators does an automatic filling machine need?',
        a: 'An automatic RFC monoblock needs just 1-2 operators for the filling section. Including blowing, packing and dispatch, a 40 BPM plant typically runs a shift with 6-8 total workers.'
      },
      {
        q: 'Can the same machine fill different bottle sizes?',
        a: 'Yes. Standard machines handle 200 ml to 2 litre bottles with changeover parts, taking 30-60 minutes per change. Confirm which change parts are included in your quotation — suppliers often charge extra for additional size sets.'
      }
    ]
  },

  'ro-plant-selection-guide': {
    intro: [
      'Your RO plant decides the one thing your brand cannot compromise: water quality. It is also the machine that BIS auditors scrutinise hardest, and the one whose running cost quietly shapes your per-bottle economics through power, membranes and reject water.',
      'This guide explains how to size an RO plant for a bottling operation, what a properly engineered treatment train includes, and how to avoid the under-specified systems that cause quality failures within the first year.'
    ],
    sections: [
      {
        heading: 'Sizing: Why Your RO Must Be Bigger Than Your Filling Line',
        paras: [
          'A common beginner mistake is matching RO output exactly to filling speed. In practice, your RO plant should produce 1.5 – 2 times your peak bottling demand, because RO systems need CIP cleaning downtime, storage tanks must refill between shifts, recovery rates fall as membranes age, and bottle rinsing consumes treated water too.',
          'Reference pairings that work: 24-30 BPM plants pair with 2000 LPH RO; 40 BPM with 3000 LPH; 60 BPM with 5000 LPH; and 120-200 BPM lines demand 10000 LPH systems, often in dual trains for redundancy.'
        ]
      },
      {
        heading: 'The Complete Treatment Train for Packaged Drinking Water',
        paras: [
          'BIS-compliant water is produced by a chain, not a single membrane. A properly engineered system includes:'
        ],
        bullets: [
          'Raw water storage and chlorine dosing for primary disinfection',
          'Multi-grade sand filter for suspended solids',
          'Activated carbon filter to remove chlorine, odour and organics',
          'Antiscalant dosing and micron cartridge filters (5 & 1 micron)',
          'High-pressure pump with SS RO membrane housings',
          'RO membranes sized for your raw water TDS and recovery target',
          'Mineral dosing / blending system to maintain palatable TDS (BIS requires TDS within specified limits)',
          'UV sterilizer and ozone generator with contact tank for final disinfection',
          'SS storage tanks with ozonated recirculation to keep stored water sterile'
        ]
      },
      {
        heading: 'Raw Water Analysis: The Non-Negotiable First Step',
        paras: [
          'Never buy an RO plant before testing your raw water. TDS, hardness, iron, silica, and microbiological load determine membrane selection, pre-treatment design and recovery rate. A system designed for 500 ppm borewell water will fail rapidly on a 2,000 ppm source; conversely, over-designing for soft water wastes capital.',
          'Insist that your supplier designs against your specific water report — and treats iron removal seriously if levels exceed 0.3 ppm, since iron fouling is the leading cause of premature membrane replacement in Indian plants.'
        ]
      },
      {
        heading: 'Operating Costs: Power, Membranes and Reject Water',
        paras: [
          'For a 3000 LPH system, expect roughly 7.5 – 10 HP of connected load for the high-pressure pump and ancillaries, membrane replacement every 2-3 years (₹60,000 – 1.2 Lakhs depending on configuration), and cartridge/consumable costs of a few thousand rupees monthly. Well-maintained systems run at 60-70% recovery — meaning 30-40% of raw water becomes reject.',
          'Plan reject reuse from day one: storage for floor washing, gardening or sale to construction users. Pollution boards increasingly ask for this plan, and it converts a waste stream into goodwill or minor revenue.'
        ]
      },
      {
        heading: 'Specification Checklist Before You Order',
        paras: [
          'Compare quotations on substance, not just LPH and price:'
        ],
        bullets: [
          'Membrane brand and count (reputed brands: Hydranautics, Toray, LG, Filmtec)',
          'SS 304 skid, piping and membrane housings — not powder-coated MS',
          'Recovery rate and permeate TDS guaranteed in writing against your water report',
          'Automatic flushing and CIP system included',
          'Instrumentation: TDS meters, flow meters, pressure gauges at each stage',
          'Ozonator capacity (grams/hour) matched to your storage and line size',
          'Commissioning, water testing and operator training included in scope'
        ]
      }
    ],
    faqs: [
      {
        q: 'Which RO plant capacity do I need for my bottling plant?',
        a: 'Size the RO at 1.5 – 2x your filling demand: 2000 LPH for 24-30 BPM plants, 3000 LPH for 40 BPM, 5000 LPH for 60 BPM, and 10000 LPH for 120-200 BPM lines. This covers rinsing usage, CIP downtime and membrane ageing.'
      },
      {
        q: 'What does an industrial RO plant cost?',
        a: 'Complete BIS-compliant treatment systems (filters, RO, UV, ozonation, tanks) range from ₹8 – 12 Lakhs for 2000 LPH to ₹30 – 45 Lakhs for 10000 LPH, depending on raw water quality and automation.'
      },
      {
        q: 'How often do RO membranes need replacement?',
        a: 'With proper pre-treatment and daily maintenance, membranes last 2-3 years. Poor iron removal or skipped CIP cleaning can cut this to under a year — pre-treatment design matters more than membrane brand.'
      },
      {
        q: 'Is ozonation mandatory for packaged drinking water?',
        a: 'IS 14543 requires effective disinfection, and the industry-standard approach is UV plus ozonation with residual ozone at filling. Ozonation also keeps stored water sterile in tanks, which BIS inspectors specifically check.'
      }
    ]
  },

  'pet-bottle-blowing-machine-guide': {
    intro: [
      'Every bottle you sell begins as a small test-tube-like PET preform. Whether you blow those preforms into bottles in-house or buy ready bottles from the market is one of the most consequential cost decisions a plant owner makes — worth ₹0.50 – 1 per bottle, which at commercial volumes means lakhs per year.',
      'This guide covers how blowing machines work, semi-automatic versus fully automatic economics, and when in-house blowing starts paying for itself.'
    ],
    sections: [
      {
        heading: 'How PET Bottle Blowing Works',
        paras: [
          'Blow moulding is a two-stage process: preforms are heated in an infrared oven until the PET reaches its stretch temperature, then stretched with a rod and inflated with high-pressure air (25-35 bar) inside a chilled mould that gives the bottle its final shape. Cycle times run from a few seconds per cavity, and mould changes let one machine produce multiple bottle designs and sizes.',
          'The machine needs three utilities: high-pressure compressed air, a chiller for the moulds, and stable power — budget these into your plant utilities from the start.'
        ]
      },
      {
        heading: 'Semi-Automatic vs Fully Automatic Blowing',
        paras: [
          'Semi-automatic machines (operator loads preforms into the oven and transfers heated preforms to the blowing station) cost ₹4.5 – 9 Lakhs for 2-cavity units producing 1,200 – 1,800 bottles/hour — a perfect match for 24-40 BPM plants. Fully automatic machines handle preform feeding, heating, blowing and ejection without contact, producing 2,400 – 12,000+ bottles/hour at ₹18 Lakhs – 1 Crore+ depending on cavities.',
          'The crossover point: once your filling line crosses 60 BPM sustained production, a fully automatic blower typically justifies itself through labour saving and output matching. Below that, semi-automatic delivers better capital efficiency.'
        ]
      },
      {
        heading: 'The In-House vs Buy-Bottles Calculation',
        paras: [
          'Market-purchased ready bottles cost ₹2.8 – 3.5 for a 1-litre bottle (and carry freight for what is mostly air). Blowing in-house, your cost is the preform (₹1.8 – 2.2) plus power and labour of roughly ₹0.15 – 0.25 — a saving of ₹0.6 – 1.0 per bottle.',
          'At 3 lakh bottles per month, in-house blowing saves ₹1.8 – 3 Lakhs monthly, paying back a ₹7 Lakh semi-automatic machine in 3-5 months. This is why virtually every serious plant blows in-house — the only exceptions are very early-stage operations validating their market.'
        ]
      },
      {
        heading: 'Specifications That Matter When Buying',
        paras: [
          'Evaluate blowing machines on these criteria rather than price alone:'
        ],
        bullets: [
          'Guaranteed output per hour at your bottle size — not theoretical cycle speed',
          'Number of cavities and mould change time',
          'Neck sizes supported (28mm PCO is standard for water bottles; jars differ)',
          'Infrared oven zones and individual lamp control for even heating',
          'High-pressure compressor and chiller included or quoted separately (a frequent hidden cost)',
          'Mould cost per design and whether one mould set is included',
          'Bottle quality consistency: wall thickness distribution and base clarity'
        ]
      },
      {
        heading: 'Common Mistakes First-Time Buyers Make',
        paras: [
          'Three pitfalls we see repeatedly: buying blowing capacity smaller than filling capacity (the filler then starves — always match or exceed it), ignoring the compressor specification (blowing needs 30+ bar high-pressure air; an undersized compressor throttles the whole line), and choosing exotic bottle designs with expensive moulds before the brand has settled — start with a proven standard design, add signature bottles once volumes justify mould investment.'
        ]
      }
    ],
    faqs: [
      {
        q: 'What does a PET bottle blowing machine cost?',
        a: 'Semi-automatic 2-cavity machines cost ₹4.5 – 9 Lakhs (1,200-1,800 bottles/hour). Fully automatic machines range from ₹18 Lakhs to over ₹1 Crore depending on cavities and speed. Add compressor and chiller if not included.'
      },
      {
        q: 'Is it cheaper to blow bottles in-house or buy ready bottles?',
        a: 'In-house blowing saves ₹0.6 – 1.0 per 1-litre bottle versus market-bought bottles. At typical plant volumes this pays back a semi-automatic machine within 3-5 months, which is why nearly all commercial plants blow in-house.'
      },
      {
        q: 'Can one blowing machine make different bottle sizes?',
        a: 'Yes — by changing moulds. One machine typically covers 200 ml to 2 litre with separate mould sets. Confirm mould change time and per-mould cost when comparing quotations.'
      },
      {
        q: 'What utilities does a blowing machine need?',
        a: 'Three essentials: high-pressure compressed air (25-35 bar), a water chiller for moulds, and stable power. Together they add roughly 15-25 HP of connected load for a semi-automatic setup — plan your electrical connection accordingly.'
      }
    ]
  },

  'complete-turnkey-mineral-water-plant-setup-guide': {
    intro: [
      'A water bottling plant is not one machine — it is a dozen systems that must work as one: water treatment, blowing, filling, capping, labelling, packaging, conveying, and quality control. Buy them piecemeal from different vendors and you inherit the integration risk: mismatched speeds, finger-pointing during breakdowns, and a commissioning phase that drags for months.',
      'A turnkey contract puts one partner in charge of the entire chain — design to commissioning. Here is what a genuine turnkey project includes, the timeline to expect, and how to evaluate a turnkey partner before signing.'
    ],
    sections: [
      {
        heading: 'What "Turnkey" Should Actually Include',
        paras: [
          'The word turnkey is used loosely in this industry. A genuine turnkey scope covers:'
        ],
        bullets: [
          'Site assessment and raw water analysis',
          'Plant layout design matched to your building and BIS hygiene requirements',
          'Complete machinery: RO treatment, blowing, rinsing-filling-capping, labelling, coding, shrink packaging, conveyors',
          'SS tanks, interconnecting piping, and electrical panel work',
          'BIS-compliant laboratory equipment package',
          'Delivery, installation, commissioning and trial production',
          'Operator training and documented SOPs',
          'Licensing guidance for BIS, FSSAI and pollution consents',
          'Defined warranty and after-sales service commitment'
        ]
      },
      {
        heading: 'The Turnkey Project Timeline, Stage by Stage',
        paras: [
          'From signed order to commercial production, a typical project runs 3-5 months:'
        ],
        table: {
          headers: ['Stage', 'Duration', 'What Happens'],
          rows: [
            ['Design & engineering', '2 – 3 weeks', 'Layout drawings, machinery specification, utility planning'],
            ['Manufacturing', '45 – 60 days', 'Machines fabricated and factory-tested; site prepared in parallel'],
            ['Installation', '2 – 4 weeks', 'Erection, piping, electrical, integration of all sections'],
            ['Commissioning & trials', '1 – 2 weeks', 'Line balancing, water quality validation, trial production'],
            ['Training & handover', '1 week', 'Operator training, SOPs, documentation handover'],
            ['Licensing (parallel)', '60 – 90 days', 'BIS, FSSAI, pollution consents processed alongside']
          ]
        }
      },
      {
        heading: 'Site Readiness: What You Prepare While Machines Are Built',
        paras: [
          'The manufacturing window is your site-preparation window. Complete these before machinery dispatch: civil flooring with proper slopes and drainage in the wet sections, three-phase power connection with the sanctioned load your supplier specifies, borewell with submersible pump and raw water storage, and basic partitioning between treatment, filling, blowing and packing zones per the approved layout.',
          'Plants that finish site work on time commission within days of machinery arrival; plants that lag turn a 4-month project into an 8-month one. Your turnkey partner should give you a site readiness checklist on day one.'
        ]
      },
      {
        heading: 'How to Evaluate a Turnkey Partner',
        paras: [
          'The machinery is only as good as the company behind it. Before choosing a partner, verify:'
        ],
        bullets: [
          'In-house manufacturing — visit the factory; traders assembling outsourced machines cannot control quality or spares',
          'Running installations you can visit and owners you can call',
          'SS 304 contact parts with material certificates, PLC-based controls',
          'Written scope covering every item — piping, panels, lab equipment and training are common exclusions in cheap quotes',
          'Service network and guaranteed response time for breakdowns',
          'BIS/FSSAI documentation experience — ask who prepares the layout and lab list for approval'
        ]
      },
      {
        heading: 'Turnkey vs Piecemeal: The Real Cost Comparison',
        paras: [
          'Buying machines separately can look 5-10% cheaper on paper. In practice, integration gaps consume that saving quickly: mismatched conveyor heights and speeds, incompatible electricals, three vendors blaming each other during a breakdown, and no single guarantee on final line output. With a turnkey contract, one party guarantees the plant produces rated output of BIS-compliant water — and that accountability is worth more than the discount.',
          'The exception: expansion projects at existing plants with strong in-house engineering teams can manage multi-vendor purchases well. For a first plant, turnkey is unambiguously the lower-risk path.'
        ]
      }
    ],
    faqs: [
      {
        q: 'What does a complete turnkey mineral water plant cost?',
        a: 'Complete turnkey projects range from ₹15 – 22 Lakhs for a 24 BPM starter plant to ₹2.5 – 3.2 Crore for a 200 BPM enterprise line — including treatment, blowing, filling, packaging, installation and training. See our capacity-wise cost guide for detailed breakdowns.'
      },
      {
        q: 'How long does a turnkey water plant project take?',
        a: 'Typically 3-5 months from order to commercial production: 45-60 days manufacturing, 2-4 weeks installation, 1-2 weeks commissioning — with licensing processed in parallel over 60-90 days.'
      },
      {
        q: 'What should I prepare at my site before machinery arrives?',
        a: 'Civil flooring with drainage, three-phase power connection at the specified load, borewell with raw water storage, and zone partitioning per the approved layout. Your turnkey partner should provide a complete site readiness checklist at order stage.'
      },
      {
        q: 'Does turnkey include licensing support?',
        a: 'A good turnkey partner provides complete documentation support for BIS and FSSAI — layout drawings, machinery lists, lab equipment package and audit preparation — though statutory fees are paid by the plant owner. Confirm this is in the written scope.'
      }
    ]
  }
};
